import { useEffect, useState } from 'react';
import { TRUTH_MARKER } from '@/utils/truthMarker';
import { getSystemIdentity } from '@/lib/api';
import { SYSTEM_BOOT_ERRORS } from '@/config/ui-invariants';

const CODES = SYSTEM_BOOT_ERRORS.codes;
const MESSAGES = SYSTEM_BOOT_ERRORS.messages;

/**
 * UI Contract Guard (PKG-014 Enforcer)
 *
 * LAW: "Rendering is illegal unless backend identity is reachable and contracts are met."
 *
 * CONTRACT (JRS-amended 2026-02-17):
 *   BLOCK if: backend unreachable OR build truth missing
 *   GRANT if: backend returns valid identity AND build truth exists
 *   SHA mismatch: advisory log only (separate deploys create transient windows)
 *
 * BEHAVIOR:
 * - Blocks render (returns null/spinner) until System Identity is verified.
 * - Fails loudly (System Lock Screen) if Backend is unreachable.
 * - Wraps the entire App.
 *
 * Error codes from SYSTEM_BOOT_ERRORS in ui-invariants.js — zero hardcoded strings.
 * Messages mirror response_taxonomy.py _SYSTEM_OUTCOMES (boot_* entries).
 */
export function UIContractGuard({ children }) {
    const [status, setStatus] = useState('VERIFYING'); // VERIFYING | GRANTED | DENIED
    const [errorCode, setErrorCode] = useState(null);

    useEffect(() => {
        const verifySystemContract = async () => {
            console.log('%c [SystemGuard] ENTRY: Starting Reality Verification ', 'background: #000; color: #fff; font-weight: bold;');

            const path = window.location.pathname;
            const module = path.includes('investors') ? 'INVESTORS' :
                path.includes('knowledge-base') ? 'KB' :
                    path.includes('startups') ? 'STARTUPS' : 'SYSTEM';

            try {
                // PHASE 3: RUNTIME REGISTRY INVARIANT
                // Fail Fast if core registries are corrupt
                if (typeof window !== 'undefined' && !window.__BUILD_TRUTH__) {
                    throw new Error(CODES.BUILD_TRUTH_MISSING);
                }

                // PKG-014: Fetch backend identity via canonical helper
                let data;
                try {
                    data = await getSystemIdentity();
                } catch (fetchErr) {
                    console.error('[SystemGuard] Backend fetch failed:', fetchErr);
                    throw new Error(CODES.BACKEND_UNREACHABLE);
                }

                const backendSha = data.runtime_commit;
                const frontendSha = window.__BUILD_TRUTH__?.sha;

                console.log(`%c [SystemGuard] IDENTITY: BE=${backendSha?.substring(0,8)} | FE=${frontendSha?.substring(0,8)} `, 'color: gray;');

                // Contract: Backend MUST be reachable and return a valid identity
                const backendValid = backendSha && backendSha.length >= 7;
                const frontendValid = frontendSha && frontendSha !== 'unknown';

                if (!backendValid) {
                    throw new Error(CODES.BACKEND_INVALID_IDENTITY);
                }

                if (!frontendValid) {
                    throw new Error(CODES.FRONTEND_INVALID);
                }

                // SHA comparison is advisory — separate deploys cause transient mismatches
                if (backendSha === frontendSha) {
                    console.log('%c [SystemGuard] MATCH: Identity Handshake Exact ', 'color: green; font-weight: bold;');
                } else {
                    console.warn('%c [SystemGuard] ADVISORY: SHA drift (BE/FE deploy window) ', 'color: orange;');
                }

                TRUTH_MARKER('IDENTITY_DECISION', module, 'PASS');
                TRUTH_MARKER('RENDER_PERMISSION', module, 'PASS');
                setStatus('GRANTED');
            } catch (e) {
                console.error('%c [SystemGuard] ERROR: System Unreachable ', 'color: red; font-weight: bold;', e);
                TRUTH_MARKER('IDENTITY_DECISION', module, 'ERROR');
                TRUTH_MARKER('RENDER_PERMISSION', module, 'DENIED');
                const code = Object.values(CODES).includes(e.message) ? e.message : CODES.BACKEND_UNREACHABLE;
                setErrorCode(code);
                setStatus('DENIED');
            }
        };

        verifySystemContract();
    }, []);

    if (status === 'VERIFYING') {
        return (
            <div style={{ padding: 20, fontFamily: SYSTEM_BOOT_ERRORS.lockScreen.fontFamily }} /* noscan — token-driven via SYSTEM_BOOT_ERRORS */>
                [SystemGuard] Verifying Runtime Contracts...
            </div>
        );
    }

    if (status === 'DENIED') {
        const userMessage = MESSAGES[errorCode] || MESSAGES[CODES.BACKEND_UNREACHABLE];
        return (
            <div style={SYSTEM_BOOT_ERRORS.lockScreen}>
                <h1 style={SYSTEM_BOOT_ERRORS.title}>
                    UI Render Permission Denied (PKG-014)
                </h1>
                <p style={SYSTEM_BOOT_ERRORS.body}>
                    {userMessage}
                </p>
                <p style={SYSTEM_BOOT_ERRORS.footer}>
                    Authority: UIContractGuard | Enforcement: PKG-014 | Code: {errorCode}
                </p>
            </div>
        );
    }

    // Grant Permission
    return children;
}
